import { featureEnabled, features } from '@sparklayer-io/spark-dash-core/constants/features';
import { createRoute, redirect } from '@tanstack/react-router';
import { loadSessionEnv } from '../../lib/session';
import { lazyPage } from '../common';
import { siteRoute } from '../layouts';
import { oauthLeaves } from './oauth';

const pages = () => import('./pages');
const platformComp = lazyPage(pages, 'PlatformHost');
const metafieldsComp = lazyPage(pages, 'MetafieldsHost');
const partnersComp = lazyPage(pages, 'PartnersHost');
const customersSyncComp = lazyPage(pages, 'CustomersSyncHost');
const customerSyncDataComp = lazyPage(pages, 'CustomerSyncDataHost');
const productsSyncComp = lazyPage(pages, 'ProductsSyncHost');
const productDataComp = lazyPage(pages, 'ProductDataHost');

// /integrations redirects to the platform page.
const integrationsIndexRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/integrations/platform', params });
  },
});

const integrationsPlatformRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/platform',
  component: platformComp,
  staticData: { title: 'Integrations - Platform' },
});

const integrationsPlatformMetafieldsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/platform/metafields',
  component: metafieldsComp,
  staticData: { title: 'Integrations - Platform - Metafields' },
});

const integrationsPartnersRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/partners',
  component: partnersComp,
  staticData: { title: 'Integrations - Partners' },
});

/** Search coercion for the customers-sync `?id=` seed: keep lone strings, fold JSON numbers back. */
const customersSyncSearch = (search: Record<string, unknown>) => ({
  id:
    typeof search.id === 'string'
      ? search.id
      : typeof search.id === 'number'
        ? String(search.id)
        : undefined,
});

/** When `CUSTOMERS_V2` is enabled the v1 customers-sync pages forward to their v2 equivalents. */
async function customersSyncV2Redirect(ctx: { params: { siteId: string; siteEnv: string } }) {
  const env = await loadSessionEnv(ctx.params.siteId, ctx.params.siteEnv, 'partner');
  if (!env) return;
  const platform = env.platform ?? env.connected_platform;
  if (featureEnabled(features.CUSTOMERS_V2, ctx.params.siteId, platform)) {
    throw redirect({ to: '/$siteId/$siteEnv/v2/customers/list', params: ctx.params });
  }
}

const integrationsCustomersSyncRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/customers-sync',
  component: customersSyncComp,
  validateSearch: customersSyncSearch,
  staticData: { title: 'Integrations - Customers Sync' },
  beforeLoad: customersSyncV2Redirect,
});

const integrationsCustomerSyncDataRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/customers-sync/$identifier',
  component: customerSyncDataComp,
  staticData: { title: 'Integrations - Customer Sync Data' },
  beforeLoad: customersSyncV2Redirect,
});

const integrationsProductsSyncRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/products-sync',
  component: productsSyncComp,
  staticData: { title: 'Integrations - Products Sync' },
});

const integrationsProductDataRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/products-sync/$variantSku',
  component: productDataComp,
  staticData: { title: 'Integrations - Product Data' },
});

// Legacy widgets URLs on the integrations tree -> the storefront widgets pages.
const integrationsWidgetsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/widgets',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/storefront/widgets', params });
  },
});

const integrationsWidgetsCoreRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/widgets/core',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/storefront/widgets/core', params });
  },
});

const integrationsWidgetsPdpRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/widgets/pdp',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/storefront/widgets/pdp', params });
  },
});

const integrationsWidgetsCssRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/widgets/css',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/storefront/widgets/css', params });
  },
});

const integrationsWidgetsTipsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'integrations/widgets/tips',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/storefront/widgets/tips', params });
  },
});

/** Integrations family leaves to attach under the site-env layout. */
export const integrationsLeaves = [
  integrationsIndexRoute,
  integrationsPlatformRoute,
  integrationsPlatformMetafieldsRoute,
  integrationsPartnersRoute,
  ...oauthLeaves,
  integrationsCustomersSyncRoute,
  integrationsCustomerSyncDataRoute,
  integrationsProductsSyncRoute,
  integrationsProductDataRoute,
  integrationsWidgetsRoute,
  integrationsWidgetsCoreRoute,
  integrationsWidgetsPdpRoute,
  integrationsWidgetsCssRoute,
  integrationsWidgetsTipsRoute,
];
